import { ChangeDetectorRef, Component, inject, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import {
    AnimationsDataModel,
    completedNotifications$,
    ConfigConstants,
    EventService,
    isNgRxEnabled,
    MATERIAL_SOURCE_CODE,
    MaterialService,
    ModalClass,
    NOTIFICATION_STATUS_CODE,
    NotificationDataInstanceToken,
    NotificationDataInterface,
    NotificationModel,
    notificationRefreshRequired,
    notificationsUpdated$,
    StatusModel,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { faChevronLeft, faChevronRight, faFlag } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { filter, mergeMap, take, takeUntil, tap } from 'rxjs/operators';
import { OverviewAnimations } from './overview-animations';
import { OverviewService } from './overview.service';
import {
    ConfirmationDataInstanceToken,
    ConfirmationDataInterface,
} from '../../confirmation/interfaces/confirmation-data.interface';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import SWIPE_ACTION = ConfigConstants.SWIPE_ACTION;

@Component({
    selector: 'pace-overview',
    templateUrl: './overview.component.html',
    styleUrls: ['./overview.component.scss'],
    animations: [OverviewAnimations.slide],
    standalone: false,
})
export class OverviewComponent implements OnInit, OnDestroy {
    public faFlag = faFlag;
    public faChevronLeft = faChevronLeft;
    public faChevronRight = faChevronRight;

    public notification: NotificationModel;
    public isFirstNotification = false;
    public isLastNotification = false;
    public allowSwipe = false;
    private swipeDirection: ConfigConstants.SWIPE_ACTION;
    private modalRef: BsModalRef;

    private readonly store = inject(Store);
    private readonly ngrxEnabled = isNgRxEnabled();
    private readonly completedNotifications$ = completedNotifications$();

    private destroy$ = new Subject<void>();
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        @Inject(NotificationDataInstanceToken) private readonly notificationService: NotificationDataInterface,
        private readonly eventService: EventService,
        private readonly materialService: MaterialService,
        private readonly overviewService: OverviewService,
        @Inject(ConfirmationDataInstanceToken) private readonly confirmationService: ConfirmationDataInterface,
        private readonly changeDetectionRef: ChangeDetectorRef,
        private readonly modalService: BsModalService,
        private readonly translate: TranslateService,
    ) {
        this.overviewService.setNotificationList();
        this.router.events
            .pipe(
                tap((event) => {
                    if (event instanceof NavigationEnd) {
                        this.ngOnInit();
                    }
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();

        // It is necessary to re-read the notification on every update to have latest values from NDP
        notificationsUpdated$()
            .pipe(
                tap(() => {
                    if (!this.notification) {
                        return;
                    }
                    // get the latest notification model with updated properties
                    const notification = this.notificationService.getNotificationByIdentifier(
                        this.notification.identifier,
                    );
                    if (!notification) {
                        throw new Error(
                            `OverviewComponent: notificationsUpdated$: Could not re-load notification for identifier ${this.notification.identifier}`,
                        );
                    }
                    this.notification = notification;
                    this.overviewService.setNotificationList();
                    this.overviewService.setCurrentNotificationIndex(this.notification);
                    this.setFirstAndLastNotificationIndicators();
                    this.changeDetectionRef.detectChanges();
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    public ngOnInit(): void {
        this.notification = <NotificationModel>this.route.snapshot.data.notification;
        this.notificationService.saveUnreadIndicator(this.notification, false).pipe(take(1)).subscribe();
        this.overviewService.setCurrentNotificationIndex(this.notification);
        this.setFirstAndLastNotificationIndicators();
        const hasMaterialsInCarousel = this.materialService
            .getAllMaterialsForNotification(this.notification.identifier)
            .some(
                (material) =>
                    material.materialSource.sourceCode === MATERIAL_SOURCE_CODE.ORDERED &&
                    material.showOnNotificationDetail,
            );
        this.allowSwipe = this.notification.participantSet.length === 0 && !hasMaterialsInCarousel;

        /**
         * Handle case where the confirmation is completed from a different device:
         * Navigate to dashboard and show a message in popup, that notification was already completed.
         */
        this.confirmationService.confirmationCompletedPreviously$
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.router.navigate([ConfigConstants.routeUrlDashboard], {}).catch((err: unknown) => {
                        this.log.error(
                            `[ConfirmationComponent] ngOnInit: Could not open Dashboard after checking completed notifications: ${JSON.stringify(err)}`,
                        );
                    });
                    this.requestNotificationRefresh();
                    if (this.modalRef) {
                        this.modalService.hide(this.modalRef.id);
                    }
                    this.modalService.show(YesNoModalDialogComponent, {
                        backdrop: 'static',
                        class: ModalClass.MODAL_MEDIUM,
                        initialState: {
                            model: new YesNoModalDialogModel(
                                <string>this.translate.instant('CONFIRMATION_ALREADY_COMPLETED_TITLE'),
                                <string>this.translate.instant('CONFIRMATION_ALREADY_COMPLETED_BODY', {
                                    notification: this.notification.identifier.toString(),
                                }),
                                <string>this.translate.instant('CONFIRMATION_ALREADY_COMPLETED_OK'),
                            ),
                        },
                        ariaLabelledBy: 'dashboard-button-modal-title',
                    });
                }),
            )
            .subscribe();

        /**
         * Listen to completed notifications. Trigger the 'confirmationCompletedFromOtherDevice$',
         * if the user navigates to a confirmation that was already completed.
         */
        this.completedNotifications$
            .pipe(
                takeUntil(this.destroy$),
                filter(
                    (completedNotifications) =>
                        !!completedNotifications.completedNotifications[this.notification.identifier.toString()],
                ),
                mergeMap(() => {
                    const now = moment();
                    const statusModel = new StatusModel(NOTIFICATION_STATUS_CODE.CRM, now, now);
                    return this.notificationService.saveStatus(this.notification, statusModel);
                }),
                tap(() => {
                    this.confirmationService.triggerConfirmationCompletedPreviously();
                }),
            )
            .subscribe();
    }

    /**
     * Asks for the notification list to be reloaded: a store dispatch once the notification domain
     * is served by the slice, and the `EventService` event the service behind
     * `NotificationDataInstanceToken` listens to while it is not.
     */
    private requestNotificationRefresh(): void {
        if (this.ngrxEnabled) {
            this.store.dispatch(notificationRefreshRequired());
            return;
        }
        this.eventService.triggerNotificationRefreshRequiredChanged(true);
    }

    public ngOnDestroy(): void {
        this.overviewService.resetCurrentNotificationIndex();
        this.destroy$.next();
    }

    public navigateToPreviousNotificationDetails(): void {
        this.navigateToNotificationDetails(true);
    }

    public navigateToNextNotificationDetails(): void {
        this.navigateToNotificationDetails(false);
    }

    private navigateToNotificationDetails(previous: boolean): void {
        this.swipeDirection = previous ? SWIPE_ACTION.RIGHT : SWIPE_ACTION.LEFT;
        let newNotification: NotificationModel | undefined;
        const currentNotificationIndex = this.overviewService.getCurrentNotificationIndex();
        const notificationsList = this.overviewService.getNotificationList();
        if (previous && currentNotificationIndex !== 0) {
            newNotification = notificationsList[currentNotificationIndex - 1];
        } else if (!previous && currentNotificationIndex !== notificationsList.length - 1) {
            newNotification = notificationsList[currentNotificationIndex + 1];
        }
        if (typeof newNotification !== 'undefined') {
            this.router
                .navigate([
                    ConfigConstants.routeUrlDetails,
                    newNotification.identifier.frontId,
                    newNotification.identifier.qmNum,
                    newNotification.identifier.maNum,
                ])
                .catch((error: Error) =>
                    this.log.error('OverviewComponent: navigateToNotificationDetails: router.navigate : ', error),
                );
        }
    }

    private setFirstAndLastNotificationIndicators(): void {
        const currentNotificationIndex = this.overviewService.getCurrentNotificationIndex();
        this.isFirstNotification = currentNotificationIndex === 0;
        this.isLastNotification = currentNotificationIndex === this.overviewService.getNotificationList().length - 1;
    }

    public getAnimationData(): AnimationsDataModel {
        const hundred = 100;
        const negativeHundred = -100;
        const offsetEnter =
            this.swipeDirection === SWIPE_ACTION.RIGHT
                ? negativeHundred
                : this.swipeDirection === SWIPE_ACTION.LEFT
                  ? hundred
                  : 0;
        // reset swipe direction
        this.swipeDirection = SWIPE_ACTION.UNKNOWN;
        return {
            value: this.overviewService.getCurrentNotificationIndex(),
            params: { offsetEnter: offsetEnter },
        };
    }
}
